import { IconArrowForward } from "@tine/designsystem-icons/sharp";
import { Grid, Group, Stack } from "@tine/designsystem-ui-react";
import { cn } from "@tine/designsystem-utils";
import type { ReactNode } from "react";
import { Link } from "wouter";
import useMediaQuery from "../../common/useMediaQuery";
import { mobileBreakpoint } from "../../common/utils";

export type ListItemProps = {
   title: string;
   titleCentered?: boolean;
   link?: string;
   onClick?: () => void;
   subtitles?: ReactNode;
   icon?: ReactNode;
   midColumn?: ReactNode;
   endColumn?: ReactNode;
   actions?: ReactNode;
   actionText?: string;
   marked?: boolean;
};

export const AccountListItemClasses = "mb-2 rounded-lg shadow-sm ";

const TitleColumn = ({ title, subtitles, icon }: ListItemProps) => {
   const subtitleList: ReactNode[] = Array.isArray(subtitles) ? subtitles : subtitles ? [subtitles] : [];
   return (
      <Group justify="start" gap={4}>
         {icon && <div className="hidden text-muted-foreground sm:block">{icon}</div>}
         <Stack justify="start" align="start" gap={0}>
            {title && <span className={cn("font-semibold", { "mb-0": !subtitles })}>{title}</span>}
            {subtitles && (
               <Stack gap={2} align="start">
                  {subtitleList.map((subtitle, index) => (
                     <span key={`${subtitle?.toString()}${index}`}>{subtitle}</span>
                  ))}
               </Stack>
            )}
         </Stack>
      </Group>
   );
};

const AccountListItemContent = ({ title, titleCentered = false, subtitles, icon, midColumn, endColumn }: ListItemProps) => {
   const isMobile = useMediaQuery(mobileBreakpoint);
   return (
      <Grid cols={3} gap={1} className={cn("w-full p-4", { "grid-cols-[1fr_fit-content(60px)]": isMobile })}>
         <Grid.Col order={1} span={isMobile ? 1 : midColumn ? 1 : 2} className={cn({ "flex items-center": titleCentered })}>
            <TitleColumn title={title} subtitles={subtitles} icon={icon} />
         </Grid.Col>
         {midColumn && (
            <Grid.Col order={{ base: 3, sm: 2 }} span={{ base: 2, sm: 1 }} className="flex items-center mt-2 sm:mt-0">
               {midColumn}
            </Grid.Col>
         )}
         {endColumn && (
            <Grid.Col order={{ base: 2, sm: 3 }} span={1} className="flex items-start justify-end sm:items-center">
               {endColumn}
            </Grid.Col>
         )}
      </Grid>
   );
};

const ArrowAndOptionalText = ({ actionText }: { actionText?: string }) => {
   return (
      <Group gap={2}>
         {actionText && <span className="hidden md:block">{actionText}</span>}
         <IconArrowForward />
      </Group>
   );
};

export const AccountListItemWithNavigation = ({
   title,
   titleCentered = false,
   subtitles,
   icon,
   midColumn,
   actionText,
   onClick,
   link,
   marked = false
}: ListItemProps) => (
   <li
      key={title}
      className={cn(AccountListItemClasses, "hover:cursor-pointer hover:bg-surface-default-hover", {
         "bg-surface-default": !marked,
         "text-button-tertiary-bg-hover": marked
      })}
      data-testid="list-item-with-navigation"
   >
      {link ? (
         <Link to={link} className="no-underline" onClick={onClick}>
            <AccountListItemContent
               title={title}
               titleCentered={titleCentered}
               subtitles={subtitles}
               icon={icon}
               midColumn={midColumn}
               endColumn={<ArrowAndOptionalText actionText={actionText} />}
            />
         </Link>
      ) : (
         <button type="button" className="text-left size-full" onClick={onClick}>
            <AccountListItemContent
               title={title}
               titleCentered={titleCentered}
               subtitles={subtitles}
               icon={icon}
               midColumn={midColumn}
               endColumn={<ArrowAndOptionalText actionText={actionText} />}
            />
         </button>
      )}
   </li>
);

export const AccountListItemWithButtonActions = ({
   title,
   titleCentered = false,
   subtitles,
   icon,
   midColumn,
   actions,
   marked = false
}: ListItemProps) => (
   <li
      className={cn(AccountListItemClasses, {
         "bg-surface-default": !marked,
         "bg-button-tertiary-bg-hover": marked
      })}
      data-testid="list-item-with-button"
   >
      <AccountListItemContent
         title={title}
         titleCentered={titleCentered}
         subtitles={subtitles}
         icon={icon}
         midColumn={midColumn}
         endColumn={actions}
      />
   </li>
);
